import React, { useState, useEffect, useMemo } from "react";
import {
  Table, Container, Row, Col, Form, Button,
  Offcanvas, Accordion, Badge, Spinner, Dropdown
} from "react-bootstrap";
import { getUserPracticeById } from "../../api";
import { getAllClaims, getInvoicesByPracticeNumbers } from "../../afinanceapi";
import { jwtDecode } from "jwt-decode";
import { useLocation, useNavigate } from "react-router-dom";
import "./CreateInvoice.css"; // Assuming you have some styles in this file
import "./acc-list.css";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function ClaimsList() {
  const [invoices, setInvoices] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Data for dropdowns
  const [practiceList, setPracticeList] = useState([]);

  // Committed filters (drive the table)
  const [selectedPractices, setSelectedPractices] = useState([]); // [] = All
  const [selectedStatuses, setSelectedStatuses] = useState([]);   // [] = All
  const [selectedMedicalAids, setSelectedMedicalAids] = useState([]); // [] = All

  // Committed dates (default 1 month ago → today)
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Temporary filters while panel is open
  const [tmpPractices, setTmpPractices] = useState([]);
  const [tmpStatuses, setTmpStatuses] = useState([]);
  const [tmpMedicalAids, setTmpMedicalAids] = useState([]);
  const [tmpStartDate, setTmpStartDate] = useState("");
  const [tmpEndDate, setTmpEndDate] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const limit = 20;

  // UI
  const [showFilters, setShowFilters] = useState(false);

  const navigate = useNavigate();
  const query = useQuery();

  const currency = new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR" });

  // Init dates once.
  // Priority: URL params (when arriving from the dashboard click-through)
  // override the default; otherwise default to the last 30 days so that
  // the report's headline window matches the dashboard widget.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlStart = params.get("startDate");
    const urlEnd = params.get("endDate");

    let startISO;
    let endISO;
    if (urlStart && urlEnd) {
      startISO = urlStart;
      endISO = urlEnd;
    } else {
      const today = new Date();
      const start = new Date();
      start.setMonth(start.getMonth() - 1);
      const iso = (d) => d.toISOString().slice(0, 10);
      startISO = iso(start);
      endISO = iso(today);
    }

    setStartDate(startISO);
    setEndDate(endISO);
    setTmpStartDate(startISO);
    setTmpEndDate(endISO);
  }, []);

  // Fetch data
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const token = sessionStorage.getItem("user");
        if (!token) throw new Error("No user token found");
        const user = jwtDecode(token);
        
        const practices = await getUserPracticeById(user._id);
        setPracticeList(practices || []);
        
        const practiceIds = (practices || []).map((p) => p._id);
        const practiceNumbers = (practices || []).map((p) => p.practiceNumber);
        
        const [claimsData, invoicesData] = await Promise.all([
          getAllClaims(practiceIds),
          getInvoicesByPracticeNumbers(practiceNumbers)
        ]);
        
        setClaims(claimsData || []);
        setInvoices(invoicesData || []);
      } catch (e) {
        console.error("Error fetching claims data:", e);
        setClaims([]);
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Create invoice lookup map
  const invoiceMap = useMemo(() => {
    return invoices.reduce((map, inv) => {
      map[inv._id] = inv;
      return map;
    }, {});
  }, [invoices]);

  // Dynamic options
  const statusOptions = useMemo(() => [
    "Processed - Item Level Warnings Or Rejections",
    "Processed",
    "Switch Rejected",
    "Approved - Claim Reversed",
    "Rejected",
    "Finalised"
  ], []);

  const medicalAidOptions = useMemo(() => {
    const set = new Set(invoices.map((inv) => inv.medicalAid?.name).filter(Boolean));
    return Array.from(set).sort();
  }, [invoices]);

  // Helpers for multi-select toggles
  const toggleIn = (arr, value) =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  const allPractices = useMemo(
    () => (practiceList || []).map((p) => p.practiceName).filter(Boolean),
    [practiceList]
  );

  const allPracticeIds = useMemo(
    () => (practiceList || []).map((p) => p._id).filter(Boolean),
    [practiceList]
  );

  // Filtered (entire dataset)
  const filteredClaims = useMemo(() => {
    // Compare on local calendar dates (YYYY-MM-DD) so the picker's chosen
    // end date includes the entire local day. Using Date objects directly
    // would parse "YYYY-MM-DD" as UTC midnight and drop same-day claims.
    const localDateOf = (iso) => {
      if (!iso) return null;
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return null;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };

    return claims.filter((claim) => {
      const inv = invoiceMap[claim.invoiceId] || {};

      // date range inclusive (local-calendar)
      const claimDateStr = localDateOf(claim.createdAt);
      const fromOK = !startDate || (claimDateStr && claimDateStr >= startDate);
      const toOK = !endDate || (claimDateStr && claimDateStr <= endDate);

      const practiceOK =
        selectedPractices.length === 0 || selectedPractices.includes(claim.practiceId);

      const statusOK =
        selectedStatuses.length === 0 || selectedStatuses.includes(claim.status);

      const medicalAidName = inv.medicalAid?.name;
      const medicalAidOK =
        selectedMedicalAids.length === 0 || 
        (medicalAidName && selectedMedicalAids.includes(medicalAidName));

      return fromOK && toOK && practiceOK && statusOK && medicalAidOK;
    });
  }, [claims, invoiceMap, selectedPractices, selectedStatuses, selectedMedicalAids, startDate, endDate]);

  // Sort the filtered set (then paginate)
  const sortedFilteredClaims = useMemo(() => {
    if (!sortConfig.key) return filteredClaims;
    const arr = [...filteredClaims];
    arr.sort((a, b) => {
      const invA = invoiceMap[a.invoiceId] || {};
      const invB = invoiceMap[b.invoiceId] || {};

      let valA = "";
      let valB = "";

      if (sortConfig.key === "invoiceNumber") {
        valA = invA.invoiceNumber || "";
        valB = invB.invoiceNumber || "";
      } else if (sortConfig.key === "accountName") {
        valA = invA.accountName || "";
        valB = invB.accountName || "";
      } else if (sortConfig.key === "medicalAid") {
        valA = invA.medicalAid?.name || "";
        valB = invB.medicalAid?.name || "";
      } else if (sortConfig.key === "status") {
        valA = a.status || "";
        valB = b.status || "";
      } else {
        valA = a[sortConfig.key] ?? "";
        valB = b[sortConfig.key] ?? "";
      }

      if (typeof valA === "string") {
        return sortConfig.direction === "asc"
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      }
      if (typeof valA === "number") {
        return sortConfig.direction === "asc" ? valA - valB : valB - valA;
      }
      return 0;
    });
    return arr;
  }, [filteredClaims, sortConfig, invoiceMap]);

  // Totals for entire filtered dataset
  const totals = useMemo(() => ({
    totalSubmitted: filteredClaims.reduce(
      (sum, claim) => sum + Number(claim.financials?.submittedAmount || 0),
      0
    ),
    totalFundPaid: filteredClaims.reduce(
      (sum, claim) => sum + (claim.totalProviderPaid || 0),
      0
    ),
    totalPatientPaid: filteredClaims.reduce(
      (sum, claim) => sum + (claim.totalPatientPaid || 0),
      0
    )
  }), [filteredClaims]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedFilteredClaims.length / limit));
  const pageStart = (page - 1) * limit;
  const pagedClaims = sortedFilteredClaims.slice(pageStart, pageStart + limit);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [selectedPractices, selectedStatuses, selectedMedicalAids, startDate, endDate]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <i className="fa-solid fa-arrow-up text-primary ms-2" />
    ) : (
      <i className="fa-solid fa-arrow-down text-primary ms-2" />
    );
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: "Claims Report", url });
      else {
        await navigator.clipboard.writeText(url);
        alert("Link copied to clipboard.");
      }
    } catch {}
  };

  // Open filter panel with current committed values loaded into temp
  const openFilters = () => {
    setTmpPractices(selectedPractices);
    setTmpStatuses(selectedStatuses);
    setTmpMedicalAids(selectedMedicalAids);
    setTmpStartDate(startDate);
    setTmpEndDate(endDate);
    setShowFilters(true);
  };

  const applyFilters = () => {
    setSelectedPractices(tmpPractices);
    setSelectedStatuses(tmpStatuses);
    setSelectedMedicalAids(tmpMedicalAids);
    setStartDate(tmpStartDate);
    setEndDate(tmpEndDate);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setSelectedPractices([]);
    setSelectedStatuses([]);
    setSelectedMedicalAids([]);
    // Reset to default date range (last month → today)
    const today = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    const iso = (d) => d.toISOString().slice(0, 10);
    const s = iso(start);
    const e = iso(today);
    setStartDate(s);
    setEndDate(e);

    // Also reset temp in case panel is open
    setTmpPractices([]);
    setTmpStatuses([]);
    setTmpMedicalAids([]);
    setTmpStartDate(s);
    setTmpEndDate(e);
  };

  const activeCount =
    (selectedPractices.length ? 1 : 0) +
    (selectedStatuses.length ? 1 : 0) +
    (selectedMedicalAids.length ? 1 : 0) +
    ((startDate && endDate) ? 1 : 0);

  return (
    <Container fluid className="pt-2 acc-list">
      {/* DESKTOP TOP-BAR (>= 768px) — totals (left) + Filter/Clear/Pagination (right) */}
      <div className="d-none d-md-block">
        <Row className="mb-2">
          <Col md={6}>
            <span className="text-primary fw-semibold">Total Claims:</span>
            <Table bordered size="sm" className="mb-0 text-center align-middle">
              <thead>
                <tr>
                  <th style={{ width: "33%" }} className="fw-semibold text-primary">Submitted</th>
                  <th style={{ width: "33%" }} className="fw-semibold text-primary">Fund Paid</th>
                  <th style={{ width: "34%" }} className="fw-semibold text-primary">Patient Paid</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="acc-list-num">{currency.format(totals.totalSubmitted)}</td>
                  <td className="acc-list-num">{currency.format(totals.totalFundPaid)}</td>
                  <td className="acc-list-num"><strong>{currency.format(totals.totalPatientPaid)}</strong></td>
                </tr>
              </tbody>
            </Table>
          </Col>
          <Col md={6} className="d-flex gap-2 flex-wrap justify-content-end align-self-end">
            <Button variant="primary" onClick={openFilters} style={{ width: "120px" }}>
              <i className="fa-solid fa-filter me-2" />
              Filter {activeCount > 0 && <Badge className="ms-2" bg="light" text="dark">{activeCount}</Badge>}
            </Button>
            <Button
              variant="outline-danger"
              onClick={clearFilters}
              style={{ width: "120px" }}
              disabled={
                selectedPractices.length === 0 &&
                selectedStatuses.length === 0 &&
                selectedMedicalAids.length === 0 &&
                !startDate &&
                !endDate
              }
            >
              <i className="fa-solid fa-trash me-2" />
              Filters
            </Button>

            {/* Pagination */}
            <div className="d-flex align-items-center gap-2 ms-2">
              <Button
                variant="primary"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <i className="fa-solid fa-angle-left" />
              </Button>
              <div className="text-nowrap small">
                Page {page} of {totalPages}
              </div>
              <Button
                variant="primary"
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <i className="fa-solid fa-angle-right" />
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      {/* MOBILE TOP-BAR (< 768px) — compact totals strip + Filter + Pagination */}
      <div className="d-md-none mb-2">
        <div className="acc-list-mobile-summary mb-2">
          <span className="acc-list-mobile-summary-label">Submitted</span>
          <span className="acc-list-mobile-summary-amount">{currency.format(totals.totalSubmitted)}</span>
          <span className="acc-list-mobile-summary-label">Fund Paid</span>
          <span className="acc-list-mobile-summary-amount">{currency.format(totals.totalFundPaid)}</span>

          <span className="acc-list-mobile-summary-label acc-list-mobile-summary-label-total">Patient Paid</span>
          <span className="acc-list-mobile-summary-amount acc-list-mobile-summary-amount-total">{currency.format(totals.totalPatientPaid)}</span>
        </div>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <Button
            variant="primary"
            onClick={openFilters}
            className="flex-grow-1"
            style={{ minWidth: 110 }}
          >
            <i className="fa-solid fa-filter me-2" />
            Filter{" "}
            {activeCount > 0 && (
              <Badge className="ms-2" bg="light" text="dark">
                {activeCount}
              </Badge>
            )}
          </Button>
          <div className="d-flex align-items-center gap-2 ms-auto">
            <Button
              variant="primary"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <i className="fa-solid fa-angle-left" />
            </Button>
            <span className="text-nowrap small">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="primary"
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <i className="fa-solid fa-angle-right" />
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading Claims...</p>
          </div>
      ) : pagedClaims.length === 0 ? (
        <p>No claims found. The default filter is for the last 7 days.</p>
      ) : (
        <>
        <div className="d-none d-md-block">
        <Table bordered hover responsive className="fixed-table">
          <thead className="text-center align-middle ms-1 me-1">
            <tr>
              <th style={{ width: "2%" }} className="text-primary fw-semibold">#</th>
              <th style={{ width: "8%" }} className="text-primary fw-semibold">Date</th>
              <th
                style={{ cursor: "pointer", width: "10%" }}
                onClick={() => handleSort("invoiceNumber")}
                className="text-primary fw-semibold"
              >
                Inv Number {renderSortIcon("invoiceNumber")}
              </th>
              <th
                style={{ cursor: "pointer", width: "18%" }}
                onClick={() => handleSort("accountName")}
                className="text-primary fw-semibold"
              >
                Account Name {renderSortIcon("accountName")}
              </th>
              <th
                style={{ cursor: "pointer", width: "12%" }}
                onClick={() => handleSort("medicalAid")}
                className="text-primary fw-semibold"
              >
                Medical Aid {renderSortIcon("medicalAid")}
              </th>
              <th
                style={{ cursor: "pointer", width: "15%" }}
                onClick={() => handleSort("status")}
                className="text-primary fw-semibold"
              >
                Claim Status {renderSortIcon("status")}
              </th>
              <th style={{ width: "12%" }} className="text-primary fw-semibold">Submitted</th>
              <th style={{ width: "12%" }} className="text-primary fw-semibold">Fund Paid</th>
              <th style={{ width: "11%" }} className="text-primary fw-semibold">Patient Paid</th>
            </tr>
          </thead>
          <tbody>
            {pagedClaims.map((claim, idx) => {
              const inv = invoiceMap[claim.invoiceId] || {};
              return (
                <tr
                  key={claim._id}
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    navigate(
                      claim.status === "Pending"
                        ? `/invoices/${claim.invoiceId}/${claim.accountId}`
                        : `/invoices/posted/${claim.invoiceId}`
                    )
                  }
                >
                  <td>{(page - 1) * limit + idx + 1}</td>
                  <td>{claim.createdAt ? claim.createdAt.split("T")[0] : ""}</td>
                  <td>{inv.invoiceNumber || ""}</td>
                  <td
                    style={{ cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (claim.accountId) navigate(`/accounts/${claim.accountId}`);
                    }}
                  >
                    {inv.accountName || ""}
                  </td>
                  <td>{inv.medicalAid?.name || ""}</td>
                  <td>{claim.status}</td>
                  <td className="text-end acc-list-num">
                    {claim.financials?.submittedAmount
                      ? currency.format(claim.financials.submittedAmount)
                      : ""
                    }
                  </td>
                  <td className="text-end acc-list-num">
                    {claim.totalProviderPaid
                      ? currency.format(claim.totalProviderPaid)
                      : ""
                    }
                  </td>
                  <td className="text-end acc-list-num">
                    {claim.totalPatientPaid
                      ? currency.format(claim.totalPatientPaid)
                      : ""
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        </div>
        {/* MOBILE CARD BRANCH (Phase 38-01) — FLAT cards, one per claim.
            Top line: invoiceNumber + submitted amount + 2-item kebab (Open Invoice / Open Account).
            Body tap mirrors desktop <tr> onClick navigation. */}
        <div className="d-md-none">
          {pagedClaims.map((claim) => {
            const inv = invoiceMap[claim.invoiceId] || {};
            return (
              <div className="acc-card" key={claim._id}>
                <div className="acc-card-top">
                  <span className="acc-card-id">{inv.invoiceNumber || "—"}</span>
                  <span className="acc-card-amount">{currency.format(claim.financials?.submittedAmount || 0)}</span>
                  <Dropdown align="end" onClick={(e) => e.stopPropagation()}>
                    <Dropdown.Toggle
                      variant="link"
                      className="acc-card-kebab text-primary text-decoration-none p-0 border-0"
                      aria-label="Row actions"
                    >
                      <i className="fa-solid fa-ellipsis-vertical" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu align="end" className="shadow-sm">
                      {/* Open Invoice — LIFT verbatim from desktop <tr> onClick at line 516 */}
                      <Dropdown.Item
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            claim.status === "Pending"
                              ? `/invoices/${claim.invoiceId}/${claim.accountId}`
                              : `/invoices/posted/${claim.invoiceId}`
                          );
                        }}
                      >
                        <i className="fa fa-file-invoice-dollar me-2" /> Open Invoice
                      </Dropdown.Item>
                      {/* Open Account — LIFT verbatim from desktop <td> onClick at line 529 */}
                      <Dropdown.Item
                        onClick={(e) => {
                          e.stopPropagation();
                          if (claim.accountId) navigate(`/accounts/${claim.accountId}`);
                        }}
                      >
                        <i className="fa fa-user me-2" /> Open Account
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                <div
                  className="acc-card-body"
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    navigate(
                      claim.status === "Pending"
                        ? `/invoices/${claim.invoiceId}/${claim.accountId}`
                        : `/invoices/posted/${claim.invoiceId}`
                    )
                  }
                >
                  <div className="acc-card-field">
                    <span className="acc-card-label">Date</span>
                    <span className="acc-card-value">{claim.createdAt ? claim.createdAt.split("T")[0] : ""}</span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Account</span>
                    <span className="acc-card-value">{inv.accountName || "—"}</span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Medical Aid</span>
                    <span className="acc-card-value">{inv.medicalAid?.name || "—"}</span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Status</span>
                    <span className="acc-card-value">{claim.status || "—"}</span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Fund / Patient Paid</span>
                    <span className="acc-card-value acc-list-num">
                      {currency.format(claim.totalProviderPaid || 0)} / {currency.format(claim.totalPatientPaid || 0)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}

      {/* Filter Offcanvas */}
      <Offcanvas show={showFilters} onHide={() => setShowFilters(false)} placement="start">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="test-primary d-flex align-items-center">
            <i className="fa-solid fa-filter me-2 text-primary" />
            Filters
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {/* Dates */}
          <div className="mb-3">
            <h6 className="mb-1 text-primary fw-normal ms-3 mb-2">Date range</h6>
            <div className="d-flex gap-2 ms-1">
              <Form.Control
                type="date"
                value={tmpStartDate}
                onChange={(e) => setTmpStartDate(e.target.value)}
              />
              <Form.Control
                type="date"
                value={tmpEndDate}
                onChange={(e) => setTmpEndDate(e.target.value)}
              />
            </div>
          </div>

          <Accordion alwaysOpen>
            {/* Practices */}
            <Accordion.Item eventKey="0">
              <Accordion.Header>Practices</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpPractices(allPracticeIds)}
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpPractices([])}
                  >
                    Clear
                  </Button>
                </div>
                <div className="d-flex flex-column" style={{ maxHeight: 240, overflowY: "auto" }}>
                  {practiceList.map((practice) => (
                    <Form.Check
                      key={practice._id}
                      type="checkbox"
                      id={`practice-${practice._id}`}
                      label={practice.practiceName}
                      checked={tmpPractices.includes(practice._id)}
                      onChange={() => setTmpPractices((arr) => toggleIn(arr, practice._id))}
                    />
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>

            {/* Statuses */}
            <Accordion.Item eventKey="1">
              <Accordion.Header>Claim Status</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpStatuses(statusOptions)}
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpStatuses([])}
                  >
                    Clear
                  </Button>
                </div>
                <div className="d-flex flex-column" style={{ maxHeight: 200, overflowY: "auto" }}>
                  {statusOptions.map((s) => (
                    <Form.Check
                      key={s}
                      type="checkbox"
                      id={`status-${s}`}
                      label={s}
                      checked={tmpStatuses.includes(s)}
                      onChange={() => setTmpStatuses((arr) => toggleIn(arr, s))}
                    />
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>

            {/* Medical Aids */}
            <Accordion.Item eventKey="2">
              <Accordion.Header>Medical Aid</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpMedicalAids(medicalAidOptions)}
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpMedicalAids([])}
                  >
                    Clear
                  </Button>
                </div>
                <div className="d-flex flex-column" style={{ maxHeight: 240, overflowY: "auto" }}>
                  {medicalAidOptions.map((name) => (
                    <Form.Check
                      key={name}
                      type="checkbox"
                      id={`medaid-${name}`}
                      label={name}
                      checked={tmpMedicalAids.includes(name)}
                      onChange={() => setTmpMedicalAids((arr) => toggleIn(arr, name))}
                    />
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {/* Actions */}
          <div className="mt-4 d-flex gap-2">
            <Button onClick={applyFilters} style={{ width: "120px" }}>
              <i className="fa-solid fa-filter me-2 text-white" />
              Apply
            </Button>
            <Button
              style={{ width: "120px" }}
              variant="outline-danger"
              onClick={() => {
                setTmpPractices([]);
                setTmpStatuses([]);
                setTmpMedicalAids([]);
                setTmpStartDate("");
                setTmpEndDate("");
              }}
            >
              <i className="fa-solid fa-trash me-2" />
              Remove
            </Button>
          </div>
          <div className="mt-2 text-muted small">
            Tip: leaving a section empty means "All".
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
}